import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchRecordingsAsync, deleteRecordingAsync } from '../store/slices/recordingsSlice';
import ConfirmationModal from './ConfirmationModal';

interface Props {
  consultationId: number;
  actions?: React.ReactNode;
}

const ConsultationRecordingsSection: React.FC<Props> = ({ consultationId, actions }) => {
  const dispatch = useAppDispatch();
  const recordings = useAppSelector((state) =>
    state.recordings.recordings.filter(r => r.consultationId === consultationId)
  );
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchRecordingsAsync({ consultationId, limit: 100 }));
  }, [consultationId, dispatch]);

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    setDeleting(true);
    try {
      await dispatch(deleteRecordingAsync(pendingDeleteId)).unwrap();
      setPendingDeleteId(null);
    } catch (_) {
      // error surfaced via slice state; keep modal open so user sees nothing happened
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="consultation-edit__card">
      <div className="consultation-recordings-head">
        <h2 className="consultation-edit__card-title">Recordings</h2>
        {actions && (
          <div className="consultation-recordings-actions">{actions}</div>
        )}
      </div>
      <div className="consultation-recordings">
        <Link
          to={`/consultations/${consultationId}/recordings/new`}
          className="btn-primary consultation-recordings-add"
        >
          + Add Recording
        </Link>

        {recordings.length === 0 ? (
          <p className="consultation-recordings-empty">No recordings linked yet.</p>
        ) : (
          <ul className="consultation-recordings-list">
            {recordings.map(r => (
              <li key={r.id} className="consultation-recordings-item">
                <Link to={`/recordings/${r.id}`}>Recording #{r.id}</Link>
                <span className="consultation-recordings-meta">
                  {r.durationSeconds != null
                    ? `${Math.floor(r.durationSeconds / 60)}m ${r.durationSeconds % 60}s · `
                    : ''}
                  {r.status}
                </span>
                <button
                  type="button"
                  className="consultation-recordings-delete"
                  onClick={() => setPendingDeleteId(r.id)}
                  title="Delete recording"
                  aria-label={`Delete recording #${r.id}`}
                >
                  🗑️
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmationModal
        isOpen={pendingDeleteId != null}
        title="Delete recording?"
        message={`This will permanently delete recording #${pendingDeleteId ?? ''} and its audio files. This cannot be undone.`}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        dangerous
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) setPendingDeleteId(null); }}
      />
    </div>
  );
};

export default ConsultationRecordingsSection;
